import { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, FileText, Target, Users, Calendar, 
  Bell, TrendingUp, Menu, X, LogOut, Settings, Moon, Sun, UserCog, Search,
  User, Shield, HelpCircle, ChevronRight, Image, Share2
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useTheme } from '../context/ThemeContext'
import NotificationBell from './NotificationBell'

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false) // Default closed on mobile
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const profileRef = useRef(null)
  const searchRef = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { isDarkMode, toggleTheme } = useTheme()

  // Set sidebar open by default on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true)
      } else {
        setSidebarOpen(false)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false)
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, keywords: ['home', 'overview', 'stats', 'analytics'] },
    { name: 'Tenders', href: '/tenders', icon: FileText, keywords: ['bid', 'rfp', 'gem', 'government', 'contract'] },
    { name: 'Proposals', href: '/proposals', icon: Target, keywords: ['quote', 'offer', 'document', 'submission'] },
    { name: 'Generate Proposal', href: '/proposals/generate', icon: Target, keywords: ['create', 'new', 'ai', 'write'] },
    { name: 'Leads', href: '/leads', icon: Users, keywords: ['contacts', 'prospects', 'crm', 'customers'] },
    { name: 'Events', href: '/events', icon: Calendar, keywords: ['meetings', 'schedule', 'calendar', 'appointments'] },
    { name: 'Alerts', href: '/alerts', icon: Bell, keywords: ['notifications', 'reminders', 'warnings'] },
    { name: 'GTM Research', href: '/gtm-research', icon: TrendingUp, keywords: ['market', 'analysis', 'strategy', 'go-to-market'] },
    { name: 'Assets', href: '/assets', icon: Image, keywords: ['images', 'diagrams', 'logos', 'media', 'upload'] },
    { name: 'Shared Docs', href: '/shared-documents', icon: Share2, keywords: ['pdf', 'ppt', 'slides', 'presentation', 'host', 'share', 'web'] },
    { name: 'Team', href: '/team', icon: UserCog, keywords: ['users', 'members', 'staff', 'management'] },
    { name: 'Settings', href: '/settings', icon: Settings, keywords: ['preferences', 'config', 'options', 'account'] },
    { name: 'Profile', href: '/profile', icon: User, keywords: ['account', 'user', 'personal', 'info'] },
  ]

  // Search functionality
  const handleSearch = (term) => {
    setSearchTerm(term)
    if (term.trim() === '') {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }
    
    const lowerTerm = term.toLowerCase()
    const results = navigation.filter(item => 
      item.name.toLowerCase().includes(lowerTerm) ||
      item.keywords?.some(k => k.toLowerCase().includes(lowerTerm))
    )
    setSearchResults(results)
    setShowSearchResults(true)
  }

  const handleSearchSelect = (href) => {
    navigate(href)
    setSearchTerm('')
    setSearchResults([])
    setShowSearchResults(false)
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarOpen(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0a] transition-colors duration-300">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Premium Glass Effect */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 sm:w-72 transform transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar Inner Container with Glass Effect */}
        <div className="h-full m-3 mr-0 flex flex-col bg-white dark:bg-[#1a1a1a]/80 dark:backdrop-blur-2xl rounded-2xl border border-gray-200 dark:border-white/[0.08] shadow-xl dark:shadow-2xl dark:shadow-black/50 overflow-hidden">
          
          {/* Logo Section */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                <span className="text-white font-bold text-lg">AI</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">BD Platform</h1>
                <p className="text-xs text-gray-500 dark:text-gray-500">Business Development</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="px-4 py-4 relative" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchTerm && setShowSearchResults(true)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-100 dark:bg-white/[0.05] border-0 rounded-xl text-sm text-gray-900 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500/50 focus:bg-white dark:focus:bg-white/[0.08] transition-all"
              />
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute left-4 right-4 top-full mt-1 bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-white/[0.08] shadow-xl dark:shadow-black/50 overflow-hidden z-50">
                {searchResults.map((item) => (
                  <button
                    key={item.href}
                    onClick={() => handleSearchSelect(item.href)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                  >
                    <item.icon className="w-4 h-4 text-gray-400" />
                    <span>{item.name}</span>
                    <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                  </button>
                ))}
              </div>
            )}
            
            {/* No Results */}
            {showSearchResults && searchTerm && searchResults.length === 0 && (
              <div className="absolute left-4 right-4 top-full mt-1 bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-white/[0.08] shadow-xl dark:shadow-black/50 p-4 z-50">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">No results found for "{searchTerm}"</p>
              </div>
            )}
          </div>

          {/* Menu Label */}
          <div className="px-6 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-600">Menu</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 pb-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-white/10">
            {navigation.filter(item => !['Settings', 'Profile'].includes(item.name)).map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <item.icon className={`w-5 h-5 mr-3 flex-shrink-0 transition-transform group-hover:scale-110 ${isActive ? 'text-white' : ''}`} />
                  <span className="truncate">{item.name}</span>
                  {isActive && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse flex-shrink-0" />
                  )}
                </Link>
              )
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-white/[0.03] mb-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg">
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-[#1a1a1a]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 truncate">{user?.role || 'BD Executive'}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:pl-64 xl:pl-72' : ''}`}>
        {/* Top Bar - Premium Glass Effect */}
        <header className="sticky top-0 z-30 mx-2 sm:mx-3 mt-2 sm:mt-3">
          <div className="bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-2xl rounded-2xl border border-gray-200 dark:border-white/[0.08] shadow-lg dark:shadow-black/20">
            <div className="flex items-center justify-between h-16 px-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                >
                  <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <div className="hidden md:block">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {navigation.find(n => n.href === location.pathname)?.name || 'Dashboard'}
                  </h2>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Theme Toggle */}
                <button 
                  onClick={toggleTheme}
                  className="p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all"
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-amber-500" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                
                <NotificationBell />

                {/* User Avatar Dropdown */}
                <div className="relative ml-2 pl-2 border-l border-gray-200 dark:border-white/10" ref={profileRef}>
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="w-9 h-9 rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform shadow-lg"
                  >
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">
                          {user?.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                    )}
                  </button>
                  
                  {/* Profile Dropdown Menu */}
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-white/[0.08] shadow-xl dark:shadow-black/50 overflow-hidden z-50">
                      {/* User Info Header */}
                      <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 dark:from-purple-500/20 dark:to-pink-500/20">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg">
                            {user?.avatar ? (
                              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                <span className="text-lg font-bold text-white">
                                  {user?.name?.charAt(0) || 'U'}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {user?.name || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user?.email || 'user@example.com'}
                            </p>
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-full">
                              <Shield className="w-3 h-3" />
                              {user?.role || 'BD Executive'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate('/profile')
                            setShowProfileMenu(false)
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-xl transition-colors"
                        >
                          <User className="w-4 h-4 text-blue-500" />
                          View Profile
                          <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                        </button>
                        <button
                          onClick={() => {
                            navigate('/settings')
                            setShowProfileMenu(false)
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-xl transition-colors"
                        >
                          <Settings className="w-4 h-4 text-gray-500" />
                          Account Settings
                          <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                        </button>
                        <button
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-xl transition-colors"
                        >
                          <HelpCircle className="w-4 h-4 text-orange-500" />
                          Help & Support
                          <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                        </button>
                      </div>
                      
                      <div className="border-t border-gray-100 dark:border-white/[0.06] p-2">
                        <button
                          onClick={() => {
                            logout()
                            setShowProfileMenu(false)
                          }}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}

export default Layout
